import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Anchor,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  message,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Steps,
  Typography,
  Tooltip,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, CloseSquareTwoTone, CheckSquareTwoTone } from '@ant-design/icons';
import dayjs from 'dayjs';
import orderService, { Order, OrderStatus } from '../services/order';
import mediaService, { MediaFile } from '../services/media';
import { getFileIconByType } from '../config/file-type-icons';
import { SERVICE_TYPES } from '../components/ServiceTypeButtons';
import { getApiResponseErrorMessage } from '../utils/error';
import { SERVICE_TYPE_MAP } from '../config/service-type-map';
import { getStatusOptionsBySubService } from '../config/status-options';
import useOrderStatusOptions from '../hooks/useOrderStatusOptions';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import DynamicOrderForm from '../components/DynamicOrderForm';
import { CommissionCalculator } from '../utils/commissionCalculator';
import userService from '../services/user';
import { NATION_OPTIONS } from '../config/nations';

// sub_services: ['registration', 'transactions', 'extension', 'postpone_evidence', 'transfer', 'review', 'revocation',
// 'search','oath','renewal','agency_transfer','cancellation','certificate_reissue','information_changes','investment'],

const { Title, Text } = Typography;

type InfoItem = {
  label: string;
  value: React.ReactNode;
  span?: number;
  roles?: string[];           // 角色可见性控制
  services?: string[];        // 限定主服务：['trademark','patent',...]
  sub_services?: string[];    // 限定子业务键：['registration','review',...]
  // 按其他字段控制可见性：返回 true 则显示
  visible_when?: (ctx: {
    order: Order | null;
    extension: Record<string, any> | null;
    service?: string;
    subService?: string;
  }) => boolean;
};

const renderBoolean = (value: any): React.ReactNode => {
  if (value === true || value === 1 || value === '1') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <CheckSquareTwoTone twoToneColor="#52c41a" />
      </span>
    );
  }
  if (value === false || value === 0 || value === '0') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <CloseSquareTwoTone twoToneColor="#ff4d4f" />
      </span>
    );
  }
  return value ?? '-';
};

function filterItemsByRole(items: InfoItem[], roles: any[]): InfoItem[] {
  const names = (roles || []).map((r: any) => (typeof r === 'string' ? r : r?.name)).filter(Boolean);
  const isAdmin = names.includes('admin');
  return (items || []).filter((it) => {
    if (!it?.roles || it.roles.length === 0) return true;
    if (isAdmin) return true;
    return it.roles.some((r) => names.includes(r));
  });
}

function GridSection({ title, items, roles, service, subService, order, extension }: {
  title: string;
  items: InfoItem[];
  roles: any[];
  service?: string;
  subService?: string;
  order?: Order | null;
  extension?: Record<string, any> | null
}) {
  // 先按角色过滤
  let shown = filterItemsByRole(items, roles);
  // 再按 service / sub_service 过滤（如有配置）
  shown = shown.filter((it) => {
    if (it.services && it.services.length > 0) {
      if (!service || !it.services.includes(service)) return false;
    }
    if (it.sub_services && it.sub_services.length > 0) {
      if (!subService || !it.sub_services.includes(subService)) return false;
    }
    return true;
  });
  // 最后按自定义条件可见性过滤（如配置了 visible_when）
  shown = shown.filter((it) => {
    if (typeof it.visible_when !== 'function') return true;
    try {
      return !!it.visible_when({ order: order ?? null, extension: extension ?? null, service, subService });
    } catch (e) {
      // 若判定异常，退化为可见，避免误隐藏
      return true;
    }
  });
  const toSpan = (raw?: number) => {
    const v = typeof raw === 'number' ? raw : 8;
    return v <= 3 ? v * 8 : v; // 兼容旧值：1/3 → 8/24
  };
  return (
    <Card title={title}>
      <Row gutter={[16, 12]}>
        {shown.map((it, idx) => (
          <Col key={`${it.label}-${idx}`} xs={24} md={toSpan(it.span)}>
            {(() => {
              const label = (it.label ?? '').toString().trim();
              const isHeading = label.toLowerCase() === 'heading';
              const hideLabel = !label || isHeading;
              return (
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  {!hideLabel && (
                    <div style={{ width: 120, color: '#666', flex: '0 0 auto' }}>{label}</div>
                  )}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      wordBreak: 'break-word',
                      fontWeight: isHeading ? 600 : undefined,
                      color: isHeading ? '#1f1f1f' : undefined,
                      fontSize: isHeading ? 15 : undefined,
                    }}
                  >
                    {it.value ?? '-'}
                  </div>
                </div>
              );
            })()}
          </Col>
        ))}
      </Row>
    </Card>
  );
}

// 商标图样预览组件：根据媒体库ID加载并显示图片缩略图
const TrademarkImagePreview: React.FC<{ id?: number; size?: number }> = ({ id, size = 80 }) => {
  const [media, setMedia] = React.useState<MediaFile | null>(null);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    const load = async () => {
      if (!id || isNaN(Number(id))) {
        setMedia(null);
        return;
      }
      try {
        setLoading(true);
        const res = await mediaService.getMedia(Number(id));
        if (res.success && res.data) {
          setMedia(res.data);
        } else {
          setMedia(null);
        }
      } catch (e) {
        setMedia(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (!id) return <span>-</span>;
  if (loading && !media) return <span style={{ color: '#999' }}>加载中...</span>;
  if (!media) return <span>-</span>;
  const url = mediaService.getMediaUrl(media);
  if (!url) return <span>-</span>;
  const name = media.title || media.original_name;
  return (
    <Tooltip
      placement="right"
      title={
        <img
          src={url}
          alt={name}
          style={{ maxWidth: 220, maxHeight: 360, display: 'block', objectFit: 'contain' }}
        />
      }
    >
      <a onClick={() => window.open(url, '_blank')}>{name}</a>
    </Tooltip>
  );
};

/**
 * 订单详情页面，用于展示主订单及其扩展信息。
 */
const OrderDetail: React.FC = () => {
  useDocumentTitle('订单详情');
  const navigate = useNavigate();
  const { id } = useParams();
  const { state: authState } = useAuth();
  const permissions = usePermissions();
  const { formattedMap: statusOptionsMap } = useOrderStatusOptions();
  const canCreate = permissions.hasActionAccess('order-management', 'create');
  const canEdit = permissions.hasActionAccess('order-management', 'edit');
  const canDelete = permissions.hasActionAccess('order-management', 'delete');
  const currentRoles = (authState?.roles || []) as any[];
  const [loading, setLoading] = useState<boolean>(true);
  const [order, setOrder] = useState<Order | null>(null);
  const [attachments, setAttachments] = useState<Array<MediaFile & { previewUrl: string }>>([]);
  const [latestStatusFiles, setLatestStatusFiles] = useState<Array<MediaFile & { previewUrl: string }>>([]);
  const [editVisible, setEditVisible] = useState(false);
  const [form] = Form.useForm();
  const modalContentRef = React.useRef<HTMLDivElement | null>(null);
  // 状态历史（用于构建水平时间轴，参照“更新状态”弹窗数据来源）
  const [status_history, setStatus_history] = useState<OrderStatus[]>([]);
  // 客户信息（根据 order.client_id 获取）
  const [user, setUser] = useState<any>(null);
  // 马德里订单编辑：指定国家列表
  const [batchSelectedNations, setBatchSelectedNations] = useState<string[]>([]);

  /**
   * 根据订单 ID 获取订单详情数据。
   */
  const fetchOrder = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    try {
      const response = await orderService.getOrder(Number(id));
      if (response.success && response.data) {
        setOrder(response.data);
      } else {
        message.error(getApiResponseErrorMessage(response, '无法获取订单详情'));
      }
    } catch (error) {
      message.error('获取订单详情时发生错误');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // 加载客户信息
  useEffect(() => {
    const loadUser = async () => {
      try {
        const clientId = (order as any)?.client_id;
        if (!clientId) {
          setUser(null);
          return;
        }
        const res = await userService.getUser(Number(clientId));
        if (res.success && res.data) {
          setUser(res.data);
        } else {
          setUser(null);
        }
      } catch (e) {
        setUser(null);
      }
    };
    loadUser();
  }, [order?.client_id]);

  // 加载状态历史，数据来源与“更新状态”弹窗一致
  useEffect(() => {
    const load = async () => {
      try {
        if (!id) return;
        const res = await orderService.getStatusHistory(Number(id));
        setStatus_history(res.success ? (res.data || []) : []);
      } catch (e) {
        setStatus_history([]);
      }
    };
    load();
  }, [id]);

  // 加载附件信息（根据 order.files 的媒体库ID）
  useEffect(() => {
    const loadAttachments = async () => {
      try {
        const raw = (order as any)?.files as any;
        let ids: number[] = [];
        if (Array.isArray(raw)) {
          ids = raw.map((v) => Number(v)).filter((n) => !isNaN(n));
        } else if (typeof raw === 'number') {
          ids = [raw];
        }
        if (!ids.length) {
          setAttachments([]);
          return;
        }
        const results = await Promise.all(ids.map((id) => mediaService.getMedia(id)));
        const ok = results.filter(r => r.success && r.data).map(r => r.data!) as MediaFile[];
        const withUrl = ok.map(m => ({ ...m, previewUrl: mediaService.getMediaUrl(m) }));
        setAttachments(withUrl);
      } catch (e) {
        setAttachments([]);
      }
    };
    loadAttachments();
  }, [order]);

  /**
   * 获取订单所属业务类型的配置信息。
   */
  const serviceInfo = useMemo(() => {
    if (!order?.service) {
      return undefined;
    }
    return SERVICE_TYPES.find((item) => item.value === order.service);
  }, [order?.service]);

  /**
   * 获取订单的扩展数据对象。
   */
  const extensionData = useMemo<Record<string, any> | null>(() => {
    if (!order?.service) {
      return null;
    }
    const extension = order[order.service as keyof Order] as Record<string, any> | undefined;
    return extension ?? null;
  }, [order]);

  // 商标图样预览：沿用列表的按需加载方式，避免刷新时拿不到 URL
  const trademarkImageCache = useRef<Map<number, string>>(new Map());

  const TrademarkImagePreview: React.FC<{ mediaId?: number }> = ({ mediaId }) => {
    const [url, setUrl] = useState<string>('');

    useEffect(() => {
      const id = mediaId ? Number(mediaId) : NaN;
      if (!id || Number.isNaN(id)) {
        setUrl('');
        return;
      }
      const cached = trademarkImageCache.current.get(id);
      if (cached !== undefined) {
        setUrl(cached);
        return;
      }
      mediaService.getMedia(id).then(res => {
        if (res.success && res.data) {
          const resolved = mediaService.getMediaUrl(res.data);
          trademarkImageCache.current.set(id, resolved);
          setUrl(resolved);
        } else {
          trademarkImageCache.current.set(id, '');
          setUrl('');
        }
      }).catch(() => {
        trademarkImageCache.current.set(id, '');
        setUrl('');
      });
    }, [mediaId]);

    if (!mediaId || !url) return <span>-</span>;
    return (
      <img
        src={url}
        alt="商标图样"
        style={{ maxWidth: 160, maxHeight: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid #f0f0f0' }}
      />
    );
  };

  // 商标图样媒体 ID：优先扩展字段的 int 值，其次主表/附件
  const trademarkMediaId = useMemo(() => {
    const candidate = (extensionData as any)?.trademark_image
      ?? (order as any)?.trademark_image;
    return (typeof candidate === 'number' && !Number.isNaN(candidate)) ? candidate : null;
  }, [extensionData, order]);

  /**
   * 构建订单状态时间轴（横向 Steps），显示方式与数据来源参考“更新状态”弹窗。
   * - 全量步骤：根据服务与子业务从 getStatusOptionsBySubService 获取
   * - 完成时间：从状态历史中按状态取最后一次出现的日期
   */
  const status_finish_time_map = useMemo(() => {
    const map: Record<string, string> = {};
    (status_history || []).forEach((s) => {
      const k = s.status || '';
      if (!k) return;
      const ts = s.created_at || '';
      if (!ts) return;
      map[k] = dayjs(ts).isValid() ? dayjs(ts).format('YYYY-MM-DD') : ts;
    });
    return map;
  }, [status_history]);

  const status_remark_map = useMemo(() => {
    const map: Record<string, string> = {};
    (status_history || []).forEach((s) => {
      const k = s.status || '';
      if (!k) return;
      const rmk = (s as any).remark || '';
      if (!rmk) return;
      map[k] = rmk;
    });
    return map;
  }, [status_history]);

  const status = useMemo(() => {
    if (!status_history || status_history.length === 0) return null;
    const sorted = [...status_history].sort((a, b) => {
      const aid = Number(a.id || 0);
      const bid = Number(b.id || 0);
      return bid - aid;
    });
    return sorted[0];
  }, [status_history]);

  const renderFileTypeIcon = useCallback((file: MediaFile | {
    file_type?: string;
    original_name?: string;
    filename?: string;
    path?: string;
    mime_type?: string
  }) => {
    const typeKey = mediaService.getFileTypeIcon(file as MediaFile);
    return getFileIconByType(typeKey);
  }, []);

  const status_label_map = useMemo(() => {
    const sub_service = (extensionData as any)?.sub_service;
    const full = getStatusOptionsBySubService(order?.service || 'trademark', sub_service, statusOptionsMap);
    return new Map(full.map(opt => [opt.value, opt.label]));
  }, [order?.service, extensionData, statusOptionsMap]);

  // 加载所有状态对应的附件
  useEffect(() => {
    const loadAllStatusFiles = async () => {
      const filesSet = new Set<number>();
      (status_history || []).forEach((item) => {
        const raw = (item as any)?.files;
        if (Array.isArray(raw)) {
          raw.forEach(v => {
            const num = Number(v);
            if (!isNaN(num)) filesSet.add(num);
          });
        } else if (typeof raw === 'string' && raw.trim() !== '') {
          raw.split(',').forEach(v => {
            const num = Number(v.trim());
            if (!isNaN(num)) filesSet.add(num);
          });
        }
      });

      const ids = Array.from(filesSet.values());
      if (!ids.length) {
        setLatestStatusFiles([]);
        return;
      }

      try {
        const results = await Promise.all(ids.map(id => mediaService.getMedia(id)));
        const ok = results.filter(r => r.success && r.data).map(r => r.data!) as MediaFile[];
        const withUrl = ok.map(m => ({ ...m, previewUrl: mediaService.getMediaUrl(m) }));
        setLatestStatusFiles(withUrl);
      } catch (e) {
        setLatestStatusFiles([]);
      }
    };

    loadAllStatusFiles();
  }, [status_history]);

  const status_steps_config = useMemo(() => {
    if (!order) return { items: [] as any[], current: 0 };
    const sub_service = (extensionData as any)?.sub_service;
    const full = getStatusOptionsBySubService(order.service || 'trademark', sub_service, statusOptionsMap);
    const labelMap = new Map(full.map(opt => [opt.value, opt.label]));

    // 仅展示已经存在记录的状态（按出现顺序，重复的状态只展示一次）
    const seen = new Set<string>();
    const historyList = (status_history || []).filter(s => {
      const key = s.status || '';
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const items = historyList.map((item, index) => {
      const title = labelMap.get(item.status || '') || item.status || '-';
      const finished_at = status_finish_time_map[item.status || ''];
      const remark = status_remark_map[item.status || ''];
      const isCurrent = index === historyList.length - 1;
      return {
        title,
        status: isCurrent ? 'process' : 'finish',
        description: (
          <div>
            <div style={{ color: '#999' }}>{finished_at || '-'}</div>
            {remark ? <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{remark}</div> : null}
          </div>
        ),
      };
    });

    return { items, current: Math.max(0, historyList.length - 1) };
  }, [order, extensionData, status_finish_time_map, status_remark_map, status_history, statusOptionsMap]);

  const summaryItems = useMemo<InfoItem[]>(() => ([
    {
      label: 'heading',
      value: '基本信息',
      span: 24
    },
    {
      label: '业务类型',
      value: serviceInfo ? (
        <Tag color={serviceInfo.color}>{serviceInfo.label.replace('服务', '')}</Tag>
      ) : (
        '-'
      ),
    },
    {
      label: '业务子类型',
      value:
        extensionData && extensionData.sub_service
          ? SERVICE_TYPE_MAP[order?.service || '']?.[extensionData.sub_service] || extensionData.sub_service
          : '-',
    },
    {
      label: '国家/地区',
      value: order?.nation || '-',
    },
    {
      label: '订单编号',
      value: order?.order_no || '-',
    },
    {
      label: '客户案件编号',
      value: order?.order_no || '-',
      span: 16
    },
    {
      label: '申请号',
      value: extensionData?.application_no || '-',
      services: ['trademark', 'patent', 'copyright'],
      sub_services: ['registration', 'transactions', 'extension', 'postpone_evidence', 'transfer', 'review', 'revocation', 'oath', 'renewal', 'agency_transfer', 'cancellation', 'information_changes', 'investment'],
    },
    {
      label: '注册号',
      value: extensionData?.registration_no || '-',
      services: ['trademark', 'copyright'],
      sub_services: ['registration', 'transactions', 'transfer', 'review', 'revocation', 'oath', 'renewal', 'agency_transfer', 'cancellation', 'certificate_reissue', 'information_changes', 'investment'],
    },
    {
      label: '国际注册号',
      value: extensionData?.international_registration_no || '-',
      services: ['trademark'],
      visible_when: ({ order }) => order?.order_type === 'madrid',
    },
    {
      label: 'heading',
      value: '具体业务信息',
      span: 24
    },
    {
      label: '商标图样',
      value: <TrademarkImagePreview mediaId={trademarkMediaId || undefined} />,
      services: ['trademark'],
      span: 24,
    },
    {
      label: '商标名称',
      value: extensionData?.trademark_name || '-',
      services: ['trademark'],
    },
    {
      label: '专利名称',
      value: extensionData?.patent_name || '-',
      services: ['patent'],
    },
    {
      label: '出售状态',
      value: extensionData?.sale_status || '-',
      services: ['trademark'],
      sub_services: ['investment'],
    },
    {
      label: '出售时间',
      value: extensionData?.sold_at ? dayjs(extensionData.sold_at).format('YYYY-MM-DD') : '-',
      services: ['trademark'],
      sub_services: ['investment'],
    },
    {
      label: '商标类型',
      value: extensionData?.trademark_type || '-',
      services: ['trademark'],
      sub_services: ['registration', 'transactions', 'investment'],
    },
    {
      label: '申请基础',
      value: extensionData?.application_basics || '-',
      services: ['trademark'],
      sub_services: ['registration'],
    },
    {
      label: '首次使用日期',
      value: extensionData?.a1_first_use_at ? dayjs(extensionData.a1_first_use_at).format('YYYY-MM-DD') : '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.application_basics === '已使用1(a)',
    },
    {
      label: '首次商用日期',
      value: extensionData?.a1_first_commercial_use_at ? dayjs(extensionData.a1_first_commercial_use_at).format('YYYY-MM-DD') : '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.application_basics === '已使用1(a)',
    },
    {
      label: '基础商标注册号',
      value: extensionData?.b44_registration_no || '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.application_basics === '本国注册44(e)',
    },
    {
      label: '基础商标注册日期',
      value: extensionData?.b44_registration_at ? dayjs(extensionData.b44_registration_at).format('YYYY-MM-DD') : '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.application_basics === '本国注册44(e)',
    },
    {
      label: '基础商标申请号',
      value: extensionData?.d44_application_no || '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.application_basics === '本国申请44(d)',
    },
    {
      label: '基础商标申请日期',
      value: extensionData?.d44_application_at ? dayjs(extensionData.d44_application_at).format('YYYY-MM-DD') : '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.application_basics === '本国申请44(d)',
    },
    {
      label: '商标含义',
      value: extensionData?.trademark_meaning || '-',
      services: ['trademark'],
      sub_services: ['registration'],
      span: 24,
    },
    {
      label: '颜色声明',
      value: extensionData?.color_statement || '-',
      services: ['trademark'],
      sub_services: ['registration'],
      span: 24,
    },
    {
      label: '大类',
      value: extensionData?.primary_class || '-',
      services: ['trademark'],
      sub_services: ['registration', 'transactions', 'extension', 'postpone_evidence', 'transfer', 'search', 'oath', 'renewal', 'agency_transfer', 'cancellation', 'certificate_reissue', 'information_changes', 'investment'],
      span: 24,
    },
    {
      label: '小类',
      value: extensionData?.sub_class || '-',
      services: ['trademark'],
      span: 24,
    },
    {
      label: '优先权',
      value: extensionData?.priority || '-',
      services: ['trademark'],
      sub_services: ['registration'],
    },
    {
      label: '优先权申请号',
      value: extensionData?.priority_no || '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.priority === '有优先权',
    },
    {
      label: '优先权申请日期',
      value: extensionData?.priority_date ? dayjs(extensionData.priority_date).format('YYYY-MM-DD') : '-',
      services: ['trademark'],
      sub_services: ['registration'],
      visible_when: ({ extension }) => extension?.priority === '有优先权',
    },
    {
      label: '商标状态',
      value: extensionData?.trademark_status ? <Tag
        color={extensionData.trademark_status === '已下证' ? 'green' : 'orange'}>{extensionData.trademark_status ? extensionData.trademark_status : '申请中'}</Tag> : '-',
      services: ['trademark'],
      sub_services: ['transfer'],
    },
    {
      label: '转让类型',
      value: extensionData?.assignment_type || '-',
      services: ['trademark'],
      sub_services: ['transfer'],
    },
    {
      label: '受让人主体类型',
      value: extensionData?.assignee_entity_type || '-',
      services: ['trademark',],
      sub_services: ['transactions'],
    },
    {
      label: '受让人名字',
      value: extensionData?.assignee_name || '-',
      services: ['trademark', 'copyright'],
      sub_services: ['transactions', 'transfer'],
    },
    {
      label: '受让人英文名字',
      value: extensionData?.assignee_name_en || '-',
      services: ['trademark', 'copyright'],
      sub_services: ['transactions', 'transfer'],
    },
    {
      label: '受让人地址',
      value: extensionData?.assignee_address || '-',
      services: ['trademark', 'copyright'],
      sub_services: ['transactions', 'transfer'],
      span: 24
    },
    {
      label: '受让人英文地址',
      value: extensionData?.assignee_address_en || '-',
      services: ['trademark'],
      sub_services: ['transactions', 'transfer'],
      span: 24
    },
    {
      label: '变更范围',
      value: extensionData?.change_items || '-',
      services: ['trademark'],
      sub_services: ['information_changes'],
    },
    {
      label: '变更后名字',
      value: extensionData?.new_applicant_name || '-',
      services: ['trademark'],
      sub_services: ['information_changes'],
    },
    {
      label: '变更后英文名字',
      value: extensionData?.new_applicant_name_en || '-',
      services: ['trademark'],
      sub_services: ['information_changes'],
    },
    {
      label: '变更后地址',
      value: extensionData?.new_applicant_address || '-',
      services: ['trademark'],
      sub_services: ['information_changes'],
      span: 24
    },
    {
      label: '变更后英文地址',
      value: extensionData?.new_applicant_address_en || '-',
      services: ['trademark'],
      sub_services: ['information_changes'],
      span: 24
    },
    {
      label: '是否含转让服务',
      value: extensionData?.include_transfer_service || '-',
      services: ['trademark'],
      sub_services: ['transactions'],
    },
    {
      label: '延期类型',
      value: extensionData?.postpone_times || '-',
      services: ['trademark'],
      sub_services: ['extension'],
    },
    {
      label: '延期次数',
      value: extensionData?.postpone_times || '-',
      services: ['trademark'],
      sub_services: ['extension'],
    },
    {
      label: '延期原因',
      value: extensionData?.postpone_reason || '-',
      services: ['trademark'],
      sub_services: ['extension'],
    },
    {
      label: '复审类型',
      value: extensionData?.review_type || '-',
      services: ['trademark'],
      sub_services: ['review'],
    },
    {
      label: '复审次数',
      value: extensionData?.review_times || '-',
      services: ['trademark'],
      sub_services: ['review'],
    },
    {
      label: '理由基础',
      value: extensionData?.rationale_basis || '-',
      services: ['trademark'],
      sub_services: ['revocation', 'review'],
    },
    {
      label: '撤销类型',
      value: extensionData?.revocation_type || '-',
      services: ['trademark'],
      sub_services: ['revocation'],
    },
    {
      label: '检索方式',
      value: extensionData?.search_method || '-',
      services: ['trademark'],
      sub_services: ['search'],
    },
    {
      label: '宣誓类型',
      value: extensionData?.oath_type || '-',
      services: ['trademark'],
      sub_services: ['oath'],
    },
    {
      label: '续展期次',
      value: extensionData?.renewal_period || '-',
      services: ['trademark'],
      sub_services: ['renewal'],
    },
    {
      label: '原代理人',
      value: extensionData?.original_agent || '-',
      services: ['trademark'],
      sub_services: ['agency_transfer'],
    },
    {
      label: '注销原因',
      value: extensionData?.cancellation_reason || '-',
      services: ['trademark'],
      sub_services: ['cancellation'],
    },
    {
      label: '证书类型',
      value: extensionData?.certificate_type || '-',
      services: ['trademark'],
      sub_services: ['certificate_reissue'],
    },

    /*
    * 专利专有字段
    * */
    {
      label: '专利类型',
      value: extensionData?.patent_type || '-',
      services: ['patent'],
      sub_services: ['authorization'],
    },

    {
      label: '是否制图',
      value: renderBoolean(extensionData?.include_drawing),
      services: ['patent'],
      sub_services: ['appearance_application'],
    },

    {
      label: '是否加速',
      value: renderBoolean(extensionData?.accelerated),
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '是否含授权',
      value: renderBoolean(extensionData?.include_authorization),
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '专利翻译',
      value: renderBoolean(extensionData?.include_translation),
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '发明人名称',
      value: extensionData?.inventor_name || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '发明人英文名称',
      value: extensionData?.inventor_name_en || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '发明人地址',
      value: extensionData?.inventor_address || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '发明人英文地址',
      value: extensionData?.inventor_address_en || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '实体类型',
      value: extensionData?.entity_type || '-',
      services: ['patent'],
      sub_services: ['authorization'],
    },

    {
      label: '优先权',
      value: extensionData?.priority || '-',
      services: ['patent'],
    },

    {
      label: 'DAS码/申请号',
      value: extensionData?.priority_no || '-',
      services: ['patent'],
      visible_when: ({ extension }) => extension?.priority === '有优先权',
    },

    {
      label: '公开状态',
      value: extensionData?.open_status || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '公开日期',
      value: extensionData?.opened_at ? dayjs(extensionData.opened_at).format('YYYY-MM-DD') : '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '公开国家和城市',
      value: extensionData?.opened_area || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '公开方式',
      value: extensionData?.opened_type || '-',
      services: ['patent'],
      sub_services: ['appearance_application', 'invention_application', 'utility_application'],
    },

    {
      label: '权利要求数量 ',
      value: extensionData?.claims_count || '-',
      services: ['patent'],
      sub_services: ['utility_application', 'invention_application'],
    },

    {
      label: '超出数量',
      value: extensionData?.exceeding_quantity || '-',
      services: ['patent'],
      sub_services: ['utility_application', 'invention_application'],
    },

    {
      label: '包含文件撰写',
      value: extensionData?.include_document_writing || '-',
      sub_services: ['patent'],
    },

    {
      label: '外观名称',
      value: extensionData?.appearance_name || '-',
      services: ['patent'],
      sub_services: ['authorization'],
    },

    {
      label: '复审类型',
      value: extensionData?.review_type || '-',
      services: ['patent'],
      sub_services: ['rejection_review'],
    },

    {
      label: '驳回原因',
      value: extensionData?.rejection_reason || '-',
      services: ['patent'],
      sub_services: ['rejection_review'],
    },

    {
      label: '复审次数',
      value: extensionData?.postpone_times || '-',
      services: ['patent'],
      sub_services: ['rejection_review'],
    },

    /*
    * 版权专有字段
    * */
    {
      label: '作品名称',
      value: extensionData?.work_name || '-',
      services: ['copyright'],
    },

    {
      label: '作品类型',
      value: extensionData?.application_category || '-',
      services: ['copyright'],
    },

    {
      label: '公开状态',
      value: extensionData?.open_status || '-',
      services: ['copyright'],
      sub_services: ['registration'],
    },

    {
      label: '公开国家和城市',
      value: extensionData?.opened_area || '-',
      services: ['copyright'],
      sub_services: ['registration'],
    },

    {
      label: '公开日期',
      value: extensionData?.opened_at || '-',
      services: ['copyright'],
      sub_services: ['registration'],
    },

    {
      label: '公开类型',
      value: extensionData?.opened_type || '-',
      services: ['copyright'],
      sub_services: ['registration'],
    },

    {
      label: '申请方式',
      value: extensionData?.application_type || '-',
      services: ['copyright'],
      sub_services: ['registration'],
    },

    {
      label: '作品灵感及来源',
      value: extensionData?.work_inspiration || '-',
      services: ['copyright'],
      sub_services: ['registration'],
    },

    /*
    * 工商服务专有字段
    * */
    {
      label: '公司编号',
      value: extensionData?.company_no || '-',
      services: ['company'],
    },

    {
      label: '公司名称',
      value: extensionData?.company_name || '-',
      services: ['company'],
    },

    {
      label: '公司类型',
      value: extensionData?.company_type || '-',
      services: ['company'],
      sub_services: ['registration', 'annual_review', 'audit'],
    },

    {
      label: '公司地址',
      value: extensionData?.company_address || '-',
      services: ['company'],
      span: 24,
      sub_services: ['registration', 'annual_review', 'audit'],
    },

    {
      label: '公司法人代表',
      value: extensionData?.legal_representative || '-',
      services: ['company'],
      sub_services: ['registration', 'annual_review', 'audit'],
    },

    {
      label: '是否含税号',
      value: renderBoolean(extensionData?.include_vat_no),
      services: ['company'],
      sub_services: ['registration'],
    },

    {
      label: '是否含开户',
      value: renderBoolean(extensionData?.include_bank_account),
      services: ['company'],
      sub_services: ['registration'],
    },

    {
      label: '是否需要外籍董事',
      value: renderBoolean(extensionData?.include_foreign_directors),
      services: ['company'],
      sub_services: ['registration'],
    },

    {
      label: '开户银行',
      value: extensionData?.account_bank || '-',
      services: ['company'],
      sub_services: ['bank_account_opening'],
    },

    {
      label: '年审次数',
      value: extensionData?.annual_inspection_times || '-',
      services: ['company'],
      sub_services: ['annual_review', 'audit'],
    },

    {
      label: '注销原因',
      value: extensionData?.cancellation_reason || '-',
      services: ['company'],
      sub_services: ['cancellation'],
    },

    {
      label: '变更项目',
      value: extensionData?.change_items || '-',
      services: ['company'],
      sub_services: ['information_changes'],
    },

    /*
    * 条形码专有字段
    * */
    {
      label: '注册号',
      value: extensionData?.application_no || '-',
      services: ['barcode'],
    },

    {
      label: '公司名称',
      value: extensionData?.company_name || '-',
      services: ['barcode'],
    },

    {
      label: '数量',
      value: extensionData?.quantity || '-',
      services: ['barcode'],
      sub_services: ['registration', 'renewal'],
    },

    {
      label: '有效期限',
      value: extensionData?.validity_period || '-',
      services: ['barcode'],
      sub_services: ['registration'],
    },

    {
      label: '续费周期',
      value: extensionData?.renewal_period || '-',
      services: ['barcode'],
      sub_services: ['annual_review', 'renewal', 'audit'],
    },

    {
      label: '注销原因',
      value: extensionData?.cancellation_reason || '-',
      services: ['barcode'],
      sub_services: ['cancellation'],
    },

    {
      label: '原始数量',
      value: extensionData?.quantity_original || '-',
      services: ['barcode'],
      sub_services: ['increase'],
    },

    {
      label: '本次增码数量',
      value: extensionData?.quantity_increase || '-',
      services: ['barcode'],
      sub_services: ['increase'],
    },

    {
      label: '业务名',
      value: extensionData?.service_name || '-',
      services: ['other'],
    },

    /*
    * 其他服务专有字段
    * */
    {
      label: 'heading',
      value: '其他信息',
      span: 24
    },

    {
      label: '下个状态',
      value: order?.next_status || '-',
      services: ['trademark'],
    },

    /*
    * 结尾公共字段
    * */
    {
      label: '截止日期',
      value: order?.due_at ? dayjs(order.due_at).format('YYYY-MM-DD') : '-',
      span: 24
    },

    // {
    //   label: '添加日期',
    //   value: order?.created_at ? dayjs(order.created_at).format('YYYY-MM-DD') : '-',
    // },
    // {
    //   label: '更新日期',
    //   value: order?.updated_at ? dayjs(order.updated_at).format('YYYY-MM-DD') : '-',
    // },
    {
      label: '备注',
      value: order?.remark ? <div style={{ whiteSpace: 'pre-wrap' }}>{order.remark}</div> : '-',
      span: 24,
    },
  ]), [extensionData, order, serviceInfo]);
  const shownSummaryItems = useMemo(() => filterItemsByRole(summaryItems, currentRoles), [summaryItems, currentRoles]);


  const orderItems = useMemo<InfoItem[]>(() => ([
    {
      label: '订单状态',
      value: order?.status ? <Tag color="blue">{order.status}</Tag> : '-',
    },
    {
      label: '付款状态',
      value: order?.payment_status ? <Tag color={order.payment_status === '已付款' ? 'green' : 'orange'}>{order.payment_status}</Tag> : '-',
      roles: ['admin', 'sales_manager', 'finance', 'sales'],
    },
    {
      label: '付款日期',
      value: order?.payment_at ? dayjs(order.payment_at).format('YYYY-MM-DD') : '-',
      roles: ['admin', 'sales_manager', 'finance', 'sales']
    },
    {
      label: '渠道',
      value: order?.channel || '-',
    },
    {
      label: '成交价格',
      value: order?.price,
      roles: ['admin', 'sales_manager', 'finance', 'sales']
    },
    {
      label: '成本',
      value: order?.cost,
      roles: ['admin', 'sales_manager', 'sales', 'finance']
    },
    {
      label: '利润',
      value: order?.profit,
      roles: ['admin', 'sales_manager', 'finance']
    },
    {
      label: '所属销售',
      value: order?.sales?.display_name || order?.sales?.username || order?.seller || '-',
      roles: ['admin', 'sales_manager']
    },
    {
      label: '本单提成',
      value: order?.commission,
      roles: ['admin', 'sales_manager']
    },
  ]), [order]);
  const shownOrderItems = useMemo(() => filterItemsByRole(orderItems, currentRoles), [orderItems, currentRoles]);


  const clientItems = useMemo<InfoItem[]>(() => ([
    {
      label: '客户名称',
      value: user?.display_name || user?.username || order?.contact_name || '-',
      span: 8,
    },
    {
      label: '客户群号',
      value: user?.group_number || '-',
      span: 8,
    },
    {
      label: '邮箱',
      value: user?.email || order?.client_email || '-',
      span: 8,
    },
    {
      label: '通知邮箱',
      value: user?.notification_emails || '-',
      roles: ['admin'],
      span: 8,
    },
    {
      label: '客户电话',
      value: user?.phone || '-',
      span: 8,
    },
    {
      label: '客户等级',
      value: user?.client_type || '-',
      span: 8,
    },
    {
      label: '客户分类',
      value: Array.isArray(user?.customer_categories)
        ? user.customer_categories.join('，')
        : '-',
      span: 8,
    },
    {
      label: '客户来源',
      value: user?.source || '-',
      span: 8,
    },
  ]), [order, user]);
  const shownClientItems = useMemo(() => filterItemsByRole(clientItems, currentRoles), [clientItems, currentRoles]);

  const applicantItems = useMemo<InfoItem[]>(() => ([
    {
      label: '申请主体类型',
      value: order?.application_entity_type || '-',
      services: ['trademark', 'copyright'],
      sub_services: ['registration', 'transactions',],
      span: 8,
    },
    {
      label: '申请人中文名称',
      value: order?.applicant_name || '-',
      span: 8,
    },
    {
      label: '申请人英文名称',
      value: order?.applicant_name_en || '-',
      span: 8,
    },
    {
      label: '申请人地址',
      value: order?.applicant_address || '-',
      span: 24,
      services: ['trademark', 'copyright'],
      sub_services: ['registration', 'transactions', 'transfer'],
    },
    {
      label: '申请人英文地址',
      value: order?.applicant_address_en || '-',
      span: 24,
      services: ['trademark', 'copyright'],
      sub_services: ['registration', 'transactions', 'transfer'],
    },
  ]), [order]);
  const shownApplicantItems = useMemo(() => filterItemsByRole(applicantItems, currentRoles), [applicantItems, currentRoles]);

  return (
    <div style={{ padding: 24, backgroundColor: '#f5f5f5', minHeight: '100%' }}>
      <Spin spinning={loading} tip="加载中...">
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space size="middle" align="center">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                  返回
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  订单详情
                </Title>
                {order?.order_no && <Text type="secondary">{order.order_no}</Text>}
              </Space>
            </Col>
            <Col>
              <Space>
                {order?.status && <Tag color="blue">{order.status}</Tag>}
                {canEdit && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={async () => {
                      if (!order) return;

                      const svc = order.service || 'trademark';
                      const ext: any = (order as any)[svc] || {};
                      const formData: any = {
                        ...order,
                        ...ext,
                        due_at: order.due_at ? dayjs(order.due_at) : null,
                        payment_at: order.payment_at ? dayjs(order.payment_at) : null,
                        created_at: order.created_at ? dayjs(order.created_at) : null,
                      };
                      ['opened_at', 'a1_first_use_at', 'a1_first_commercial_use_at', 'b44_registration_at', 'd44_application_at']
                        .forEach((k) => {
                          if (formData[k]) formData[k] = dayjs(formData[k]);
                        });

                      // 如果是马德里订单，加载子订单的国家列表
                      if (order.order_type === 'madrid' && order.id) {
                        try {
                          const res = await orderService.getOrders({ parent_id: order.id, per_page: 1000 } as any);
                          if (res?.success && Array.isArray(res.data?.data)) {
                            const nations = (res.data.data as any[])
                              .map((o: any) => o?.nation)
                              .filter((n: any) => typeof n === 'string' && n.trim().length > 0);
                            setBatchSelectedNations(Array.from(new Set(nations)) as any);
                          } else {
                            setBatchSelectedNations([]);
                          }
                        } catch (e) {
                          setBatchSelectedNations([]);
                        }
                      } else {
                        setBatchSelectedNations([]);
                      }

                      form.setFieldsValue(formData);
                      setEditVisible(true);
                    }}
                  >
                    编辑
                  </Button>
                )}
              </Space>
            </Col>
          </Row>

          {order ? (
            <>
              <GridSection title="业务信息" items={shownSummaryItems} roles={currentRoles} service={order?.service}
                subService={(extensionData as any)?.sub_service} order={order} extension={extensionData} />

              <GridSection title="订单信息" items={shownOrderItems} roles={currentRoles} service={order?.service}
                subService={(extensionData as any)?.sub_service} order={order} extension={extensionData} />

              <GridSection title="客户信息" items={shownClientItems} roles={currentRoles} service={order?.service}
                subService={(extensionData as any)?.sub_service} order={order} extension={extensionData} />

              {order?.service !== 'barcode' && order?.service !== 'company' && order?.service !== 'other' && (
                <GridSection title="申请/权利/持有人信息" items={shownApplicantItems} roles={currentRoles} service={order?.service}
                  subService={(extensionData as any)?.sub_service} order={order} extension={extensionData} />
              )}

              <Card title="状态跟踪">
                {status_steps_config.items.length > 0 ? (
                  <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                    <Steps
                      size="small"
                      direction="horizontal"
                      labelPlacement="vertical"
                      style={{ width: 'max-content', minWidth: '100%' }}
                      current={status_steps_config.current}
                      items={status_steps_config.items}
                    />
                  </div>
                ) : (
                  <Empty description="暂无状态记录" />
                )}

                {status && (
                  <div style={{ marginTop: 16 }}>
                    <Descriptions size="small" column={1} title="最新状态信息">
                      {/*<Descriptions.Item label="截止日期">{status?.due_at ? dayjs(status.due_at).format('YYYY-MM-DD') : '-'}</Descriptions.Item>*/}
                      <Descriptions.Item
                        label="下个状态">{status?.next_status ? (status_label_map.get(status.next_status) || status.next_status) : '-'}</Descriptions.Item>
                      <Descriptions.Item label="文件">{latestStatusFiles.length > 0 ? `${latestStatusFiles.length} 个` : '无'}</Descriptions.Item>
                    </Descriptions>

                    {latestStatusFiles.length > 0 ? (
                      <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                        {latestStatusFiles.map((m) => (
                          <Col key={m.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                            <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                              <div style={{
                                width: '100%',
                                height: 120,
                                backgroundColor: '#fafafa',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {m.file_type === 'image' && m.previewUrl ? (
                                  <img src={m.previewUrl} alt={m.title || m.original_name}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                ) : (
                                  <div style={{ padding: 12, textAlign: 'center' }}>
                                    <div style={{ fontSize: 28, marginBottom: 4 }}>{renderFileTypeIcon(m)}</div>
                                    <div style={{ fontSize: 12, color: '#999' }}>{m.file_type || '文件'}</div>
                                  </div>
                                )}
                              </div>
                              <div style={{ padding: 8 }}>
                                <div style={{ fontSize: 12, color: '#333', marginBottom: 4 }}>{m.title || m.original_name || `文件 #${m.id}`}</div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <a href={m.previewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#1890ff' }}>预览</a>
                                  <span onClick={() => mediaService.downloadMedia(m.id, m.title || m.original_name)} style={{ fontSize: 14, color: '#1890ff', cursor: 'pointer' }}>下载</span>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    ) : null}
                  </div>
                )}
              </Card>

              <Card title="附件">
                {attachments.length === 0 ? (
                  <Empty description="暂无附件" />
                ) : (
                  <Row gutter={[16, 16]}>
                    {attachments.map((m) => (
                      <Col key={m.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                        <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                          <div
                            style={{ width: '100%', height: 120, backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {m.file_type === 'image' && m.previewUrl ? (
                              <img src={m.previewUrl} alt={m.title || m.original_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : (
                              <div style={{ padding: 12, textAlign: 'center' }}>
                                <div style={{ marginBottom: 4 }}>{renderFileTypeIcon(m)}</div>
                                <div style={{ color: '#999', fontSize: 12 }}>{m.file_type || '文件'}</div>
                              </div>
                            )}
                          </div>
                          <div style={{ padding: '8px 8px 4px 8px' }} title={m.title || m.original_name}>
                            <div style={{
                              fontSize: 12,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>{m.title || m.original_name || m.filename}</div>
                            <div style={{ color: '#999', fontSize: 12 }}>{typeof m.file_size === 'number' ? `${(m.file_size / 1024).toFixed(2)} KB` : ''}</div>
                          </div>
                          <div style={{ padding: '4px 8px 8px 8px', display: 'flex', gap: 8 }}>
                            <a href={m.previewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#1890ff' }}>预览</a>
                            <span onClick={() => mediaService.downloadMedia(m.id, m.title || m.original_name)} style={{ fontSize: 14, color: '#1890ff', cursor: 'pointer' }}>下载</span>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card>
            </>
          ) : (
            <Card>
              <Empty description="未找到对应的订单记录" />
            </Card>
          )}
        </Space>
      </Spin>

      {/* 编辑弹窗 */}
      <Modal
        title={order ? `编辑订单 - ${order.order_no || ''}` : '编辑订单'}
        open={editVisible}
        onCancel={() => {
          setEditVisible(false);
          form.resetFields();
        }}
        onOk={async () => {
          if (!canEdit) {
            message.error('无权限编辑订单');
            return;
          }

          try {
            const values = await form.validateFields();
            const svc = order?.service || 'trademark';
            const calc = await CommissionCalculator.calculate({ price: values.price, cost: values.cost, client_type: values.client_type });
            const fmt = (d?: any) => (d && typeof d.format === 'function') ? d.format('YYYY-MM-DD') : d;
            const fmtDateTime = (d?: any) => (d && typeof d.format === 'function') ? d.format('YYYY-MM-DD HH:mm:ss') : d;
            const payload: any = {
              ...values,
              service: svc,
              profit: calc.profit?.toString?.(),
              commission: calc.commission?.toString?.(),
              due_at: fmtDateTime(values.due_at),
              payment_at: fmtDateTime(values.payment_at),
              created_at: fmtDateTime(values.created_at),
              opened_at: fmt(values.opened_at),
              a1_first_use_at: fmt(values.a1_first_use_at),
              a1_first_commercial_use_at: fmt(values.a1_first_commercial_use_at),
              b44_registration_at: fmt(values.b44_registration_at),
              d44_application_at: fmt(values.d44_application_at),
            };
            if (order?.id) {
              const resp = await orderService.updateOrder(order.id, payload);
              if (resp.success) {
                // 如果是马德里父订单，处理子订单的创建/删除
                if (order.order_type === 'madrid' && Array.isArray(batchSelectedNations)) {
                  try {
                    const childrenRes = await orderService.getOrders({ parent_id: order.id, per_page: 1000 } as any);
                    const children: any[] = (childrenRes?.success ? (childrenRes.data?.data || []) : []) as any[];

                    // 现有子单：nation -> ids
                    const existingMap = new Map<string, number[]>();
                    for (const c of children) {
                      const nat = typeof c?.nation === 'string' ? c.nation : '';
                      if (!nat) continue;
                      if (!existingMap.has(nat)) existingMap.set(nat, []);
                      if (c?.id) existingMap.get(nat)!.push(c.id as number);
                    }
                    const existingNations = new Set<string>(Array.from(existingMap.keys()));
                    const selectedSet = new Set<string>(batchSelectedNations.filter((n) => typeof n === 'string' && n));

                    // 获取父订单的 sub_service
                    const parentSubService = payload.sub_service || 'registration';

                    // 需要新增 = 选中的 - 已有的
                    const toCreate = Array.from(selectedSet).filter((n) => !existingNations.has(n));
                    if (toCreate.length > 0 && canCreate) {
                      const { getOrderPrefix, generateOrderNoWithPrefix } = await import('../config/order-no-prefix');
                      const orderNoPrefix = getOrderPrefix(order.service || 'trademark', parentSubService);

                      const baseData: any = {
                        ...payload,
                        id: undefined,
                        parent_id: order.id,
                        service: 'trademark',
                        sub_service: parentSubService,
                        order_type: 'madrid_child',
                        registration_no: values.international_registration_no,
                      };

                      await Promise.allSettled(toCreate.map((nation) => {
                        const childPayload = {
                          ...baseData,
                          nation,
                          order_no: generateOrderNoWithPrefix(orderNoPrefix),
                        } as any;
                        return orderService.createOrder(childPayload);
                      }));
                    }

                    // 需要删除 = 已有的 - 选中的
                    const toDeleteNations = Array.from(existingNations).filter((n) => !selectedSet.has(n));
                    if (toDeleteNations.length > 0 && canDelete) {
                      const toDeleteIds: number[] = toDeleteNations.flatMap((n) => existingMap.get(n) || []);
                      if (toDeleteIds.length > 0) {
                        await Promise.allSettled(toDeleteIds.map((id) => orderService.deleteOrder(id)));
                      }
                    }
                  } catch (e) {
                    console.warn('处理马德里子订单失败', e);
                  }
                }

                message.success('保存成功');
                setEditVisible(false);
                form.resetFields();
                fetchOrder();
              } else {
                message.error(resp.message || '保存失败');
              }
            }
          } catch (e: any) {
            message.error(e?.response?.data?.message || '保存失败');
          }
        }}
        width={1000}
        destroyOnClose
        maskClosable={false}
      >
        {order && (
          <Row gutter={24}>
            <Col span={5}>
              <Anchor
                items={[
                  { key: 'business', href: '#section-business', title: '基本信息' },
                  { key: 'applicant', href: '#section-applicant', title: '申请人信息' },
                  ...(order.order_type === 'madrid'
                    ? [{ key: 'nations', href: '#section-nations', title: '指定国家' }]
                    : []),
                  { key: 'client', href: '#section-client', title: '客户信息' },
                  { key: 'price', href: '#section-price', title: '财务信息' },
                  { key: 'files', href: '#section-files', title: '文件' },
                ]}
                affix
                targetOffset={8}
                getContainer={() => modalContentRef.current as unknown as HTMLElement}
              />
            </Col>
            <Col span={19}>
              <div ref={modalContentRef} style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
                <Form form={form} layout="vertical" initialValues={{ service: order.service, payment_status: order.payment_status || '未付款' }}>
                  <Form.Item name="files" hidden />
                  <DynamicOrderForm
                    serviceType={order.service || 'trademark'}
                    form={form}
                    isEdit
                    anchorMode
                    extraTabsAfterBasic={(() => {
                      const isMadrid = order.order_type === 'madrid';
                      if (!isMadrid) return undefined as any;
                      return {
                        key: 'nations',
                        label: '指定国家',
                        children: (
                          <div>
                            <div style={{ marginBottom: 8, color: '#666' }}>选择要创建子订单的国家：</div>
                            <Select
                              mode="multiple"
                              placeholder="选择国家"
                              style={{ width: '100%' }}
                              value={batchSelectedNations}
                              onChange={(vals) => setBatchSelectedNations(vals as string[])}
                              options={NATION_OPTIONS}
                            />
                            <div style={{ marginTop: 16 }}>
                              <Form.Item label="国际注册号" name="international_registration_no">
                                <Input placeholder="请输入国际注册号（可选）" />
                              </Form.Item>
                            </div>
                          </div>
                        ),
                      };
                    })()}
                  />
                </Form>
              </div>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default OrderDetail;
